import { useEffect, useMemo, useRef, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import {
  IDueMaterial,
  IDueMaterialGridFilter,
  IDueMaterialGridFilterSchema,
} from "../api/types";
import {
  Box,
  Button,
  Chip,
  debounce,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { AutoCompleteElement } from "components/AutoComplete";
import RadioButtonGroup from "components/RadioButtonGroup";
import DatePickerElement from "components/DatePickerElement";
import {
  IAsyncDropdownModel,
  IDropdownModel,
} from "common/models/IDropdownModel";
import { formatDateTimeIST } from "utils/date";
import { FilterOutlined, MailOutlined } from "@ant-design/icons";
import useAuth from "hooks/useAuth";
import { showError, showSuccess } from "utils/showMessages";
import LoadingButton from "@mui/lab/LoadingButton";
import StyledDataGrid from "components/@extended/StyledDataGrid";
import { GetMaterialTypeValues } from "features/materialtype/api/hooks";
import { GetMemberTypeValues } from "features/memberType/api/hooks";
import useDashboardStore from "../store/DashboardStore";
import { GetDueMaterial } from "../api/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { SelectInstance } from "react-select";
import asyncRequests from "common/requests/asyncRequests";
import AsyncSelect from "react-select/async";
import {
  SendOverDueEmail,
  SendSelectedOverdueEmail,
} from "features/email/api/mutation";
import { AiFillFileExcel } from "react-icons/ai";
import api from "utils/api";
import dayjs from "dayjs";
import { ExportButton } from "components/ExportButton";
const DueMaterialGrid = () => {
  const { modules } = useAuth();
  const emailMutation = SendOverDueEmail();
  const apiURL = "api/Reports/dueMaterialExports";
  const fileName = "DueMaterialExports";
  const [dueMaterialExportloading, setDueMaterialExportLoading] =
    useState(false);
  const {
    dueMaterialPostModel,
    dueMemberData,
    setDueMemberData,
    handleDueMaterialFiltering,
    handleDueMaterialPagination,
    handleDueMaterialSorting,
  } = useDashboardStore();

  const { data: getDueMaterials, isFetching } =
    GetDueMaterial(dueMaterialPostModel);

  const columns: GridColDef<IDueMaterial>[] = useMemo(
    () => [
      {
        field: "memberType",
        headerName: "Member Type",
        flex: 0.8,
      },
      {
        field: "institute",
        headerName: "Institute",
        flex: 0.8,
      },
      {
        field: "title",
        headerName: "Material",
        flex: 1.5,
      },
      {
        field: "materialType",
        headerName: "Material Type",
        flex: 0.8,
      },

      {
        field: "accessionNo",
        headerName: "Accession No",
        flex: 1,
      },
      {
        field: "authorName",
        headerName: "Author Name",
        flex: 1,
      },
      {
        field: "memberName",
        headerName: "Member Name",
        flex: 1.5,
      },
      {
        field: "memberCode",
        headerName: "Member Code",
        flex: 1.3,
      },
      {
        field: "issueDate",
        headerName: "Issue Date",
        filterable: false,
        flex: 0.6,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          return (
            <>
              {params.value !== null
                ? formatDateTimeIST(params.value)
                : "-"}
            </>
          );
        },
      },
      {
        field: "dueDate",
        headerName: "Due Date",
        filterable: false,
        flex: 0.6,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          return (
            <>
              {params.value !== null
                ? formatDateTimeIST(params.value)
                : "-"}
            </>
          );
        },
      },
      {
        field: "dueDay",
        headerName: "Due Days",
        flex: 0.5,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          return (
            <>
              {params?.value > 0 ? (
                <>
                  <Typography
                    component="div"
                    sx={{
                      color: "red",
                    }}
                  >
                    {params.value}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    component="div"
                    sx={{
                      color: "green",
                    }}
                  >
                    {params.value * -1}
                  </Typography>
                </>
              )}
            </>
          );
        },
      },
      {
        field: "fineAmount",
        headerName: "Fine Amount",
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          return (
            <>
              {params?.value > 0 ? (
                <>
                  <Typography
                    component="div"
                    sx={{
                      color: "red",
                    }}
                  >
                    {params.value.toLocaleString("en-IN")} Rs.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    component="div"
                    sx={{
                      color: "green",
                    }}
                  >
                    0 Rs.
                  </Typography>
                </>
              )}
            </>
          );
        },
      },
    ],
    [],
  );

  const [selectedIssueIDs, setSelectedIssueIDs] = useState<(string | number)[]>(
    [],
  );
  const [sendingSelectedEmail, setSendingSelectedEmail] = useState(false);
  const sendSelectedEmailMutation = SendSelectedOverdueEmail();
  const [activePreset, setActivePreset] = useState<string>("all");

  useEffect(() => {
    if (getDueMaterials?.apiData?.data) {
      setDueMemberData(getDueMaterials?.apiData?.data);
    }
  }, [getDueMaterials?.apiData?.data]);

  const dueStatusOptions = [
    { id: 1, label: "Overdue Accessions" },
    { id: 2, label: "Not Overdue Accessions (Issued Materials)" },
  ];

  const methods = useForm<IDueMaterialGridFilter>({
    mode: "all",
    resolver: zodResolver(IDueMaterialGridFilterSchema),
  });

  const {
    register,
    reset,
    handleSubmit,
    control,
    setValue,
    getValues,
    watch, 
    formState: { errors, isSubmitting },
  } = methods;

 
  const fromDueDate = watch("fromDueDate");
  const toDueDate = watch("toDueDate");
  const fromIssueDate = watch("fromIssueDate");
  const toIssueDate = watch("toIssueDate");

  useEffect(() => {
    if (fromDueDate && toDueDate) {
      const from = dayjs(fromDueDate);
      const to = dayjs(toDueDate);
      if (from.isValid() && to.isValid() && from.isAfter(to, "day")) {
        setValue("toDueDate", fromDueDate);
      }
    }
  }, [fromDueDate, toDueDate, setValue]);

  useEffect(() => {
    if (fromIssueDate && toIssueDate) {
      const from = dayjs(fromIssueDate);
      const to = dayjs(toIssueDate);
      if (from.isValid() && to.isValid() && from.isAfter(to, "day")) {
        setValue("toIssueDate", fromIssueDate);
      }
    }
  }, [fromIssueDate, toIssueDate, setValue]);

  const onSubmit = handleSubmit(async (data) => {
    if (data.accessionNoValue) {
      data.materialAccessionNoID = String(data.accessionNoValue.value);
    } else {
      data.materialAccessionNoID = null;
    }
    if (data.memberCodeValue) {
      data.memberID = String(data.memberCodeValue.value);
    } else {
      data.memberID = null;
    }
    handleDueMaterialFiltering(data);
  });

  const handlePresetClick = (presetKey: string) => {
    setActivePreset(presetKey);
    const currentValues = getValues();
    const updatedFilter: IDueMaterialGridFilter = {
      ...currentValues,
      dueStatus: null,
      dueDays: null,
      remainingDays: null,
      fineAmount: null,
    };

    if (presetKey === "overdue") {
      updatedFilter.dueStatus = 1;
    } else if (presetKey === "overdue7") {
      updatedFilter.dueStatus = 1;
      updatedFilter.dueDays = 7;
    } else if (presetKey === "overdue30") {
      updatedFilter.dueStatus = 1;
      updatedFilter.dueDays = 30;
    } else if (presetKey === "dueToday") {
      updatedFilter.dueStatus = 2;
      updatedFilter.remainingDays = 0;
    } else if (presetKey === "dueNext3") {
      updatedFilter.dueStatus = 2;
      updatedFilter.remainingDays = 3;
    } else if (presetKey === "dueNext7") {
      updatedFilter.dueStatus = 2;
      updatedFilter.remainingDays = 7;
    } else if (presetKey === "withFine") {
      updatedFilter.fineAmount = 1;
    }

    setValue("dueStatus", updatedFilter.dueStatus ?? null);
    setValue("dueDays", updatedFilter.dueDays ?? null);
    setValue("remainingDays", updatedFilter.remainingDays ?? null);
    setValue("fineAmount", updatedFilter.fineAmount ?? null);

    if (updatedFilter.accessionNoValue) {
      updatedFilter.materialAccessionNoID = String(
        updatedFilter.accessionNoValue.value,
      );
    } else {
      updatedFilter.materialAccessionNoID = null;
    }
    if (updatedFilter.memberCodeValue) {
      updatedFilter.memberID = String(updatedFilter.memberCodeValue.value);
    } else {
      updatedFilter.memberID = null;
    }
    handleDueMaterialFiltering(updatedFilter);
  };

  const handleClear = () => {
    accessionRef.current?.clearValue();
    membercodeRef.current?.clearValue();
    setActivePreset("all");
    setSelectedIssueIDs([]);
    handleDueMaterialFiltering({
      memberTypeID: null,
      accessionNoValue: null,
      memberID: null,
      memberCodeValue: null,
      materialTypeID: null,
      dueDays: null,
      materialAccessionNoID: null,
      dueStatus: null,
      remainingDays: null,
      fineAmount: null,
      fromDueDate: null,
      toDueDate: null,
      fromIssueDate: null,
      toIssueDate: null,
    });
    setDueMemberData([]);
  };

  useEffect(() => {
    reset(dueMaterialPostModel.filterModel);
  }, [dueMaterialPostModel.filterModel, reset]);

  const rowCountRef = useRef(getDueMaterials?.apiData?.count || 0);
  const rowCount = useMemo(() => {
    if (getDueMaterials?.apiData?.count !== undefined) {
      rowCountRef.current = getDueMaterials?.apiData?.count;
    }
    return rowCountRef.current;
  }, [getDueMaterials?.apiData?.count]);

  const [loading, setLoading] = useState(false);
  const { data: memberTypeQuery } = GetMemberTypeValues();
  const [memberTypeOptions, setMemberTypeOptions] = useState<IDropdownModel[]>(
    [],
  );
  useEffect(() => {
    setMemberTypeOptions(memberTypeQuery?.apiData || []);
  }, [memberTypeQuery]);

  const { data: materialTypeQuery } = GetMaterialTypeValues();
  const [materialTypeOptions, setMaterialTypeOptions] = useState<
    IDropdownModel[]
  >([]);
  useEffect(() => {
    setMaterialTypeOptions(materialTypeQuery?.apiData || []);
  }, [materialTypeQuery]);

  const onEmailSubmit = handleSubmit(() => {
    if (isSubmitting) return;
    setLoading(true);
    emailMutation.mutate(null, {
      onSettled(response) {
        if (response?.apiData == true) {
          setLoading(false);
          showSuccess("Mail Sended to all Members!!");
        } else if (response?.status !== 200) {
          showError("Failed to send email. Please try again later.");
          setLoading(false);
        }
      },
    });
  });

  const handleSendSelectedOverdueEmail = () => {
    if (selectedIssueIDs.length === 0) return;
    setSendingSelectedEmail(true);
    sendSelectedEmailMutation.mutate(selectedIssueIDs, {
      onSuccess: () => {
        showSuccess("Overdue reminder email(s) sent successfully.");
        setSelectedIssueIDs([]);
      },
      onError: (err: any) => {
        showError(err?.message || "Failed to send overdue reminder email(s).");
      },
      onSettled: () => {
        setSendingSelectedEmail(false);
      },
    });
  };

  const accessionRef = useRef<SelectInstance<IAsyncDropdownModel> | null>(null);
  const membercodeRef = useRef<SelectInstance<IAsyncDropdownModel> | null>(
    null,
  );

  const accessionOptions: IAsyncDropdownModel[] = [];
  const handleAccessionSearch = debounce(async (query: string, callback) => {
    const options = await asyncRequests.getAccessionNo(query, null);
    callback(options.apiData || []);
  }, 500);

  const memberOptions: IAsyncDropdownModel[] = [];
  const handleMemberSearch = debounce(async (query: string, callback) => {
    const options = await asyncRequests.getMemberCode(query, null);
    callback(options.apiData || []);
  }, 500);

  const handleExport = handleSubmit((data) => {
    setDueMaterialExportLoading(true);
    api.downloadExcel(apiURL, fileName, data, setDueMaterialExportLoading);
  });

  return (
    <>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Grid
          container
          sx={{ justifyContent: "space-between", alignItems: "flex-start" }}
        >
          <Grid item>
            <Typography component="div" variant="h3" marginBlockEnd={2}>
              Due Materials
            </Typography>
          </Grid>
          <Grid item sx={{ alignContent: "flex-end" }}>
            {modules?.notificationEmailLog?.permissions.Add ? (
              <>
                <Stack
                  visibility={
                    getDueMaterials?.apiData?.count > 0 ? "visible" : "hidden"
                  }
                  direction={"row"}
                  spacing={3}
                  sx={{ justifyContent: "center", alignItems: "center" }}
                >
                  <Typography component="div">
                    <Grid container color="red">
                      <Grid>Note: </Grid>
                      <Grid sx={{ ml: 0.8, fontWeight: 600 }}>
                        Email can trigger only those members who have Overdue
                        Materials!!!
                      </Grid>
                    </Grid>
                  </Typography>
                  <LoadingButton
                    size="medium"
                    variant="contained"
                    startIcon={<MailOutlined />}
                    onClick={onEmailSubmit}
                    loading={loading}
                    disabled={getDueMaterials?.apiData?.count <= 0}
                  >
                    Send Overdue Email (All Members)
                  </LoadingButton>
                </Stack>
              </>
            ) : (
              <></>
            )}
          </Grid>
        </Grid>
        <Divider sx={{ mb: 2 }} />

        {/* Quick Filter Preset Chips */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1, fontWeight: 600, color: "text.secondary" }}
          >
            Quick Filters:
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
            <Chip
              label="All"
              clickable
              color={activePreset === "all" ? "primary" : "default"}
              variant={activePreset === "all" ? "filled" : "outlined"}
              onClick={() => handlePresetClick("all")}
            />
            <Chip
              label="Overdue Materials"
              clickable
              color={activePreset === "overdue" ? "error" : "default"}
              variant={activePreset === "overdue" ? "filled" : "outlined"}
              onClick={() => handlePresetClick("overdue")}
            />
            <Chip
              label="Overdue > 7 Days"
              clickable
              color={activePreset === "overdue7" ? "error" : "default"}
              variant={activePreset === "overdue7" ? "filled" : "outlined"}
              onClick={() => handlePresetClick("overdue7")}
            />
            <Chip
              label="Overdue > 30 Days"
              clickable
              color={activePreset === "overdue30" ? "error" : "default"}
              variant={activePreset === "overdue30" ? "filled" : "outlined"}
              onClick={() => handlePresetClick("overdue30")}
            />
            <Chip
              label="Due Today"
              clickable
              color={activePreset === "dueToday" ? "success" : "default"}
              variant={activePreset === "dueToday" ? "filled" : "outlined"}
              onClick={() => handlePresetClick("dueToday")}
            />
            <Chip
              label="Due in Next 3 Days"
              clickable
              color={activePreset === "dueNext3" ? "success" : "default"}
              variant={activePreset === "dueNext3" ? "filled" : "outlined"}
              onClick={() => handlePresetClick("dueNext3")}
            />
            <Chip
              label="Due in Next 7 Days"
              clickable
              color={activePreset === "dueNext7" ? "success" : "default"}
              variant={activePreset === "dueNext7" ? "filled" : "outlined"}
              onClick={() => handlePresetClick("dueNext7")}
            />
            <Chip
              label="With Fine (>= ₹1)"
              clickable
              color={activePreset === "withFine" ? "warning" : "default"}
              variant={activePreset === "withFine" ? "filled" : "outlined"}
              onClick={() => handlePresetClick("withFine")}
            />
          </Stack>
        </Box>

        <Box component="form" noValidate sx={{ mt: 1 }} onSubmit={onSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <RadioButtonGroup
                row
                control={control}
                name="memberTypeID"
                label="Member Type"
                options={memberTypeOptions}
                labelKey="name"
                valueKey="id"
                emptyOptionLabel="All"
              />
            </Grid>
            <Grid item xs={12}>
              <RadioButtonGroup
                row
                control={control}
                name="dueStatus"
                label="Due Status"
                options={dueStatusOptions}
                type="number"
                emptyOptionLabel="All"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <AutoCompleteElement
                options={materialTypeOptions}
                control={control}
                name="materialTypeID"
                label="Material Type"
                placeholder="Select Material type"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <TextField
                fullWidth
                {...register("dueDays", {
                  setValueAs: (v) =>
                    v === "" || v === null || v === undefined
                      ? null
                      : Number(v),
                })}
                label="Overdue Days"
                placeholder="Enter Overdue Days"
                defaultValue=""
                autoComplete="off"
                type="number"
                error={!!errors.dueDays}
                helperText={errors.dueDays ? errors.dueDays?.message : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <TextField
                fullWidth
                {...register("remainingDays", {
                  setValueAs: (v) =>
                    v === "" || v === null || v === undefined
                      ? null
                      : Number(v),
                })}
                label="Remaining Days"
                placeholder="Enter Remaining Days"
                // defaultValue=""
                autoComplete="off"
                type="number"
                error={!!errors.remainingDays}
                helperText={
                  errors.remainingDays ? errors.remainingDays?.message : ""
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <TextField
                fullWidth
                {...register("fineAmount", {
                  setValueAs: (v) =>
                    v === "" || v === null || v === undefined
                      ? null
                      : Number(v),
                })}
                label="Min Fine Amount (₹)"
                placeholder="Enter Min Fine Amount"
                // defaultValue=""
                autoComplete="off"
                type="number"
                error={!!errors.fineAmount}
                helperText={errors.fineAmount ? errors.fineAmount?.message : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <DatePickerElement
                control={control}
                name="fromDueDate"
                label="From Due Date"
                format="DD-MM-YYYY"
                maxDate={
                  toDueDate && dayjs(toDueDate).isValid()
                    ? dayjs(toDueDate)
                    : undefined
                }
                sx={{ width: "100%" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <DatePickerElement
                control={control}
                name="toDueDate"
                label="To Due Date"
                format="DD-MM-YYYY"
                minDate={
                  fromDueDate && dayjs(fromDueDate).isValid()
                    ? dayjs(fromDueDate)
                    : undefined
                }
                sx={{ width: "100%" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <DatePickerElement
                control={control}
                name="fromIssueDate"
                label="From Issue Date"
                format="DD-MM-YYYY"
                maxDate={
                  toIssueDate && dayjs(toIssueDate).isValid()
                    ? dayjs(toIssueDate)
                    : undefined
                }
                sx={{ width: "100%" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <DatePickerElement
                control={control}
                name="toIssueDate"
                label="To Issue Date"
                format="DD-MM-YYYY"
                minDate={
                  fromIssueDate && dayjs(fromIssueDate).isValid()
                    ? dayjs(fromIssueDate)
                    : undefined
                }
                sx={{ width: "100%" }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={4}>
              <Controller
                name="accessionNoValue"
                control={control}
                render={({ field: { onChange, value, ref } }) => (
                  <AsyncSelect
                    ref={accessionRef}
                    blurInputOnSelect
                    defaultValue={accessionOptions}
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      control: (baseStyles) => ({
                        ...baseStyles,
                        borderColor: errors.accessionNoValue
                          ? "red"
                          : baseStyles.borderColor,
                      }),
                    }}
                    inputRef={ref}
                    getOptionLabel={(item) => item.label}
                    getOptionValue={(item) => item.value}
                    loadOptions={handleAccessionSearch}
                    noOptionsMessage={({ inputValue }) =>
                      !inputValue ? "Type Something..." : "No record found"
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuPlacement="bottom"
                    menuShouldScrollIntoView={false}
                    placeholder="Enter Accession No. / Title / Material Code"
                    value={value}
                    isClearable
                    onChange={(selected) => onChange(selected)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={4}>
              <Controller
                name="memberCodeValue"
                control={control}
                render={({ field: { onChange, value, ref } }) => (
                  <AsyncSelect
                    ref={membercodeRef}
                    blurInputOnSelect
                    defaultValue={memberOptions}
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      control: (baseStyles) => ({
                        ...baseStyles,
                        borderColor: errors.memberCodeValue
                          ? "red"
                          : baseStyles.borderColor,
                      }),
                    }}
                    inputRef={ref}
                    getOptionLabel={(item) => item.label}
                    getOptionValue={(item) => item.value}
                    loadOptions={handleMemberSearch}
                    noOptionsMessage={({ inputValue }) =>
                      !inputValue ? "Type Something..." : "No record found"
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuPlacement="bottom"
                    menuShouldScrollIntoView={false}
                    placeholder="Enter Member Name / Member Code"
                    value={value}
                    isClearable
                    onChange={(selected) => onChange(selected)}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Button
            title="Filter"
            type="submit"
            variant="contained"
            sx={{ mt: 2, mr: 1 }}
            disabled={isSubmitting}
            startIcon={<FilterOutlined />}
          >
            Filter
          </Button>
          <Button
            title="Clear"
            type="reset"
            variant="contained"
            color="secondary"
            sx={{ mt: 2 }}
            onClick={() => {
              handleClear();
            }}
          >
            Clear
          </Button>
        </Box>
      </Paper>

      <Paper elevation={3} sx={{ mt: 2, px: 2, py: 2 }}>
        <Grid
          container
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mb: 1,
            gap: 1,
          }}
        >
          {modules?.notificationEmailLog?.permissions?.Add && (
            <Tooltip title="Send overdue reminder email to selected members">
              <span>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  disabled={
                    selectedIssueIDs.length === 0 || sendingSelectedEmail
                  }
                  loading={sendingSelectedEmail}
                  startIcon={<MailOutlined />}
                  onClick={handleSendSelectedOverdueEmail}
                >
                  Send Overdue Reminder (Email)
                  {selectedIssueIDs.length > 0
                    ? ` (${selectedIssueIDs.length})`
                    : ""}
                </LoadingButton>
              </span>
            </Tooltip>
          )}
          {modules?.dueMaterialExport?.permissions?.Add && (
            <ExportButton
              groupName="DueMaterialExport"
              fileName="DueMaterialExports"
              reportTitle="Due Materials Report"
              params={() => {
                const filter = dueMaterialPostModel.filterModel;
                return {
                  materialTypeID: filter.materialTypeID
                    ? Number(filter.materialTypeID)
                    : null,
                  memberTypeID: filter.memberTypeID
                    ? Number(filter.memberTypeID)
                    : null,
                  dueDays: filter.dueDays ?? null,
                  remainingDays: filter.remainingDays ?? null,
                  fineAmount: filter.fineAmount ?? null,
                  fromDueDate: filter.fromDueDate ?? null,
                  toDueDate: filter.toDueDate ?? null,
                  fromIssueDate: filter.fromIssueDate ?? null,
                  toIssueDate: filter.toIssueDate ?? null,
                  materialAccessionNoID: filter.materialAccessionNoID
                    ? Number(filter.materialAccessionNoID)
                    : null,
                  memberID: filter.memberID ? Number(filter.memberID) : null,
                  dueStatus: filter.dueStatus ?? null,
                };
              }}
              disabled={rowCount === 0}
              buttonText="Export"
            />
          )}
        </Grid>
        <Grid container sx={{ height: 450 }}>
          <StyledDataGrid
            sx={{
              "& .MuiDataGrid-columnHeaderTitle": {
                whiteSpace: "wrap",
                fontWeight: 600,
                lineHeight: "normal",
                fontSize: "13px",
              },
              "& .MuiDataGrid-cell": {
                whiteSpace: "wrap",
                alignContent: "center",
                fontSize: 14,
              },
            }}
            density="compact"
            checkboxSelection
            rowSelectionModel={selectedIssueIDs}
            onRowSelectionModelChange={(newModel) =>
              setSelectedIssueIDs(newModel as (string | number)[])
            }
            isRowSelectable={(params) => (params.row.dueDay ?? 0) > 0}
            disableRowSelectionOnClick
            getRowId={(item) => item.uniqueID}
            columns={columns}
            pageSizeOptions={[10, 20, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { ...dueMaterialPostModel.pagingModel },
              },
            }}
            sortModel={dueMaterialPostModel.sortModel}
            onSortModelChange={handleDueMaterialSorting}
            onPaginationModelChange={handleDueMaterialPagination}
            getRowHeight={() => "auto"}
            paginationMode="server"
            sortingMode="server"
            rows={getDueMaterials?.apiData?.data}
            rowCount={rowCount}
            disableColumnFilter
            disableColumnSorting
            loading={isFetching}
          />
        </Grid>
      </Paper>
    </>
  );
};

export default DueMaterialGrid;
